import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronRight, AlertTriangle, Lightbulb, Rocket } from 'lucide-react';
import StaticPageLayout from '../../../components/StaticPageLayout';
import { useThemeStore } from '../../../store/theme';

const sections = [
  { id: 'overview', title: 'Overview' },
  { id: 'when-it-happens', title: 'You might see this in SweetPilot Employees when:' },
  { id: 'how-to-deal', title: 'How can we deal with these "hallucinations"?' },
  { id: 'whats-next', title: "What's next?" },
];

const relatedArticles = [
  { title: 'Which AI Models does SweetPilot use?', href: '/doc/article/ai-models' },
  { title: 'What is SweetPilot?', href: '/doc/article/what-is-sweetpilot' },
  { title: 'Brain AI explained', href: '/doc/collections/brain-ai' },
  { title: 'How to get started with SweetPilot', href: '/doc/article/getting-started' },
  { title: 'Multiple language support in SweetPilot App', href: '/doc/article/multi-language-support' },
];

export default function AIHallucinations() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [activeSection, setActiveSection] = useState('overview');

  // SEO Schema markup
  const schemaMarkup = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Understanding AI Hallucinations - What They Are and How to Prevent Them',
    description: 'Learn what AI hallucinations are, why they happen in SweetPilot employees, and best practices to minimize inaccurate AI responses. Practical tips included.',
    author: { '@type': 'Organization', name: 'SweetPilot' },
    publisher: { '@type': 'Organization', name: 'SweetPilot', logo: { '@type': 'ImageObject', url: 'https://sweetpilot.com/logo.png' } },
    datePublished: '2024-01-01',
    dateModified: new Date().toISOString().split('T')[0],
    mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://sweetpilot.com/doc/article/ai-hallucinations' }
  };

  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = sections.map(section => ({
        id: section.id,
        element: document.getElementById(section.id),
      }));

      const currentSection = sectionElements.find(section => {
        if (section.element) {
          const rect = section.element.getBoundingClientRect();
          return rect.top <= 150 && rect.bottom >= 150;
        }
        return false;
      });

      if (currentSection) {
        setActiveSection(currentSection.id);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <>
      <Helmet>
        <title>Understanding AI Hallucinations - Prevention Guide | SweetPilot Help Center</title>
        <meta name="description" content="Learn what AI hallucinations are and why they occur. Discover best practices to minimize inaccurate AI responses and get more reliable results from SweetPilot." />
        <meta name="keywords" content="AI hallucinations, AI accuracy, AI reliability, SweetPilot accuracy, AI errors, prevent AI mistakes, AI best practices, reliable AI" />
        <meta property="og:title" content="Understanding AI Hallucinations - SweetPilot Guide" />
        <meta property="og:description" content="What are AI hallucinations and how to minimize them for better results." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://sweetpilot.com/doc/article/ai-hallucinations" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI Hallucinations Explained" />
        <meta name="twitter:description" content="Learn to prevent AI hallucinations and get reliable results." />
        <link rel="canonical" href="https://sweetpilot.com/doc/article/ai-hallucinations" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
      </Helmet>

      <StaticPageLayout title="" subtitle="" showBackButton={false}>
        {/* Breadcrumbs */}
        <nav className="mb-8">
          <ol className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
          <li>
            <Link to="/docs" className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}>
              All Collections
            </Link>
          </li>
          <li><ChevronRight className="w-4 h-4" /></li>
          <li>
            <Link to="/doc/collections/getting-started" className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}>
              Getting started
            </Link>
          </li>
          <li><ChevronRight className="w-4 h-4" /></li>
          <li>
            <Link to="/doc/collections/getting-started" className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}>
              General info
            </Link>
          </li>
          <li><ChevronRight className="w-4 h-4" /></li>
          <li className={isDark ? 'text-white' : 'text-slate-900'}>
            Understanding AI Hallucinations
          </li>
        </ol>
      </nav>

      <div className="flex gap-10">
        {/* Main Content */}
        <div className="flex-1 max-w-3xl">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h1 className={`text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Understanding AI Hallucinations
            </h1>
            <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
              Why They Happen and How to Work With Them?
            </p>
            <div className={`flex items-center gap-3 mt-4 text-sm ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                <span className="text-sm">S</span>
              </div>
              <div>
                <span>Written by </span>
                <span className={isDark ? 'text-white' : 'text-slate-900'}>Support Team</span>
              </div>
              <span>•</span>
              <span>Updated recently</span>
            </div>
          </motion.div>

          {/* Overview Section */}
          <motion.section
            id="overview"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-10"
          >
            <p className={`text-lg leading-relaxed mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <strong className={isDark ? 'text-white' : 'text-slate-900'}>Ever wonder why AI sometimes just makes stuff up?</strong> It's called "hallucination," and it's a quirky part of how these amazing systems work.
            </p>

            <p className={`leading-relaxed mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Think about SweetPilot Employees, which are super smart thanks to models like GPT and Claude for text, and Imagen and DALL-E for visuals. They're incredible, but sometimes they get a little <em>too</em> creative.
            </p>

            <p className={`leading-relaxed mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <strong className={isDark ? 'text-white' : 'text-slate-900'}>So, what exactly is an AI hallucination?</strong> It's when the AI generates something that sounds totally confident and coherent, but it's actually wrong, misleading, or just plain made up. Like an employee spouting incorrect tasks it can do, or an image generator showing you shoes when you asked for a forest.
            </p>

            <p className={`leading-relaxed mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <strong className={isDark ? 'text-white' : 'text-slate-900'}>Why does this happen?</strong> It's not really a "mistake" in the human sense; it's more about how AI learns and predicts:
            </p>

            <ol className={`list-decimal list-inside space-y-4 mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li>
                <strong className={isDark ? 'text-white' : 'text-slate-900'}>Playing a guessing game:</strong> Models like GPT and Claude are essentially trying to guess the next word based on all the data they've seen. Sometimes their guess is spot-on, and sometimes it's... not.
              </li>
              <li>
                <strong className={isDark ? 'text-white' : 'text-slate-900'}>Missing pieces:</strong> If the AI's training data is incomplete or out-of-date, it might try to fill in the blanks with something that sounds plausible but isn't true.
              </li>
              <li>
                <strong className={isDark ? 'text-white' : 'text-slate-900'}>Vague instructions:</strong> If you're not super clear with your prompt, the AI might go off on a tangent that sounds good but misses your point entirely.
              </li>
              <li>
                <strong className={isDark ? 'text-white' : 'text-slate-900'}>Image mix-ups:</strong> For image generators like DALL-E or Imagen, it's like they're struggling to translate your words into visuals, leading to irrelevant or distorted pictures.
              </li>
            </ol>
          </motion.section>

          {/* When It Happens Section */}
          <motion.section
            id="when-it-happens"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-10"
          >
            <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              You might see this in SweetPilot Employees when:
            </h2>

            <div className={`p-5 rounded-xl border mb-4 ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg ${isDark ? 'bg-orange-500/20' : 'bg-orange-100'}`}>
                  <AlertTriangle className={`w-6 h-6 ${isDark ? 'text-orange-400' : 'text-orange-600'}`} />
                </div>
                <div>
                  <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Doing tasks
                  </h3>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                    An Employee might invent tasks, abilities, or things it simply can't do.
                  </p>
                </div>
              </div>
            </div>

            <div className={`p-5 rounded-xl border ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                  <Lightbulb className={`w-6 h-6 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                </div>
                <div>
                  <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Generating images
                  </h3>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                    You ask for a "forest scene," and it keeps showing you shoes! It's likely misinterpreting your request or having internal alignment issues.
                  </p>
                </div>
              </div>
            </div>
          </motion.section>

          {/* How To Deal Section */}
          <motion.section
            id="how-to-deal"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-10"
          >
            <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              How can we deal with these "hallucinations"?
            </h2>

            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              While we can't get rid of them completely, we can definitely manage them:
            </p>

            <ul className={`space-y-4 mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li className="flex items-start gap-3">
                <span className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isDark ? 'bg-purple-400' : 'bg-purple-600'}`}></span>
                <p>
                  <strong className={isDark ? 'text-white' : 'text-slate-900'}>Be super specific:</strong> Give the AI clear, detailed prompts with lots of context.
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isDark ? 'bg-purple-400' : 'bg-purple-600'}`}></span>
                <p>
                  <strong className={isDark ? 'text-white' : 'text-slate-900'}>Always double-check:</strong> Never just assume the AI is right. Fact-check text and review images before you share them.
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isDark ? 'bg-purple-400' : 'bg-purple-600'}`}></span>
                <p>
                  <strong className={isDark ? 'text-white' : 'text-slate-900'}>Try, try again:</strong> If you don't get what you want the first time, open a new chat and rephrase your prompt. Even small tweaks can make a huge difference.
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isDark ? 'bg-purple-400' : 'bg-purple-600'}`}></span>
                <p>
                  <strong className={isDark ? 'text-white' : 'text-slate-900'}>Use your Brain AI wisely:</strong> Storing important info there can help keep things consistent across tasks.
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isDark ? 'bg-purple-400' : 'bg-purple-600'}`}></span>
                <p>
                  <strong className={isDark ? 'text-white' : 'text-slate-900'}>Know what it can do:</strong> Understand the specific features and integrations SweetPilot offers, rather than just assuming.
                </p>
              </li>
            </ul>
          </motion.section>

          {/* What's Next Section */}
          <motion.section
            id="whats-next"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-10"
          >
            <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              What's next?
            </h2>

            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              AI is always evolving, and new models are being developed to reduce hallucinations by using better training data and linking to factual sources. Even though hallucinations are part of the AI world for now, understanding them helps us use these incredible tools more effectively.
            </p>

            <div className={`p-5 rounded-xl border ${isDark ? 'bg-green-500/10 border-green-500/20' : 'bg-green-50 border-green-200'}`}>
              <div className="flex items-start gap-3">
                <Rocket className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                <div className={`${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                  <p>
                    <strong className={isDark ? 'text-white' : 'text-slate-900'}>Bottom line:</strong> Hallucinations aren't a sign that AI is failing; they're just a reminder that AI is a predictive system, not a magical oracle. By learning to work with them, we can really unleash the power of SweetPilot Employees while staying aware of their limitations.
                  </p>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Divider */}
          <hr className={`my-10 ${isDark ? 'border-white/10' : 'border-slate-200'}`} />

          {/* Related Articles */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-10"
          >
            <h2 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Related Articles
            </h2>
            <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
              {relatedArticles.map((article, index) => (
                <Link
                  key={article.title}
                  to={article.href}
                  className={`flex items-center justify-between p-4 transition-colors ${
                    index !== relatedArticles.length - 1
                      ? isDark ? 'border-b border-white/5' : 'border-b border-slate-100'
                      : ''
                  } ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}
                >
                  <span className={isDark ? 'text-white' : 'text-slate-900'}>{article.title}</span>
                  <ChevronRight className={`w-5 h-5 ${isDark ? 'text-gray-600' : 'text-slate-400'}`} />
                </Link>
              ))}
            </div>
          </motion.section>

          {/* Feedback */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className={`text-center p-8 rounded-xl border ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}
          >
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Did this answer your question?
            </p>
            <div className="flex justify-center gap-4">
              <button className="text-3xl hover:scale-110 transition-transform" title="Not helpful">
                😞
              </button>
              <button className="text-3xl hover:scale-110 transition-transform" title="Somewhat helpful">
                😐
              </button>
              <button className="text-3xl hover:scale-110 transition-transform" title="Very helpful">
                😃
              </button>
            </div>
          </motion.div>
        </div>

        {/* Sidebar TOC */}
        <div className="hidden lg:block w-64 flex-shrink-0">
          <div className="sticky top-24">
            <nav className={`border-l ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                    activeSection === section.id
                      ? isDark
                        ? 'text-white border-l-2 border-purple-500 -ml-px bg-purple-500/10'
                        : 'text-purple-600 border-l-2 border-purple-600 -ml-px bg-purple-50'
                      : isDark
                        ? 'text-gray-400 hover:text-white'
                        : 'text-slate-500 hover:text-slate-900'
                  }`}
                >
                  {section.title}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </div>
      </StaticPageLayout>
    </>
  );
}
